import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History, Undo2, Loader2, AlertTriangle, Bot, User } from "lucide-react";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtTs = (s) => (s ? new Date(s).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const TYPE = { receipt: "Beleg", bill: "Eingangsrechnung", invoice: "Kundenrechnung" };

/** Protokoll der letzten Bank-Zuordnungen – jede Zeile lässt sich rückgängig machen (alles geht zurück wie vorher). */
export function BankMatchActivityDialog({ companyId, open, onOpenChange, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/bank/match-activity?limit=40`);
      setItems(r.data.items || []);
    } catch { toast.error("Protokoll konnte nicht geladen werden"); } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const undo = async (row) => {
    setBusyId(row.candidate_id);
    try {
      await apiClient.delete(`/bank-transactions/${row.journal_entry_id}/match`);
      toast.success(`Zuordnung rückgängig: «${row.label}» ist wieder offen, Bankbewegung wie vor der Zuordnung`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Rückgängig fehlgeschlagen"); } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="bank-match-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-indigo-600" /> Letzte Zuordnungen</DialogTitle>
          <DialogDescription>Was zuletzt Belegen/Rechnungen zugeordnet wurde – manuell oder durch die KI-Verbuchung. «Rückgängig» löst die Zuordnung und stellt die Bankbewegung wieder her.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto">
          {loading && <p className="text-sm text-slate-500 p-4" data-testid="bank-match-activity-loading">Lade …</p>}
          {!loading && items.length === 0 && <p className="text-sm text-slate-500 p-4" data-testid="bank-match-activity-empty">Noch keine Zuordnungen.</p>}
          <ul className="divide-y divide-slate-100">
            {items.map((r) => (
              <li key={`${r.type}-${r.candidate_id}`} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid={`bank-match-activity-row-${r.candidate_id}`}>
                <span className="text-xs text-slate-500 w-32 shrink-0">{fmtTs(r.matched_at)}</span>
                <span className="inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 shrink-0 border-slate-200 text-slate-600" title={r.matched_by}>
                  {r.matched_by === "KI-Verbuchung" ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />} {r.matched_by}
                </span>
                <div className="flex-1 min-w-[16rem]">
                  <div className="font-medium text-slate-900 truncate">
                    {TYPE[r.type]} «{r.label}» · CHF {fmt(r.amount)}{r.is_refund ? " (Rückerstattung)" : ""}
                  </div>
                  <div className="text-xs text-slate-500 truncate" title={r.bank_description}>
                    → Bank {r.bank_date} · {r.bank_amount > 0 ? "+" : ""}{fmt(r.bank_amount)} · {r.bank_description}
                  </div>
                </div>
                {r.amount_mismatch && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid={`bank-match-activity-mismatch-${r.candidate_id}`}>
                    <AlertTriangle className="h-3 w-3" /> Betrag weicht ab
                  </span>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busyId === r.candidate_id} onClick={() => undo(r)} data-testid={`bank-match-activity-undo-${r.candidate_id}`}>
                  {busyId === r.candidate_id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Undo2 className="h-3 w-3 mr-1" />} Rückgängig
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
